import { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  FormHelperText,
  Grid,
  InputLabel,
  Stack,
  styled,
  useMediaQuery,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { HighlightOffOutlined } from "@mui/icons-material";
import { useProposalContext } from "../..";
import { Setting } from "../../../../utils/Setting";
import Images from "../../../../config/images";
import theme from "../../../../config/theme";
import CInput from "../../../../components/CInput";
import CAutocomplete from "../../../../components/CAutocomplete";
import {
  getApiData,
  getAPIProgressData,
  postFormData,
} from "../../../../utils/APIHelper";
import { isArray, isEmpty } from "lodash";
import ImageUploader from "./ImageUploader";
import CustomerSelection from "./CustomerSelection";
import SearchLocation from "./SearchLocation";

const errorObj = {
  typeErr: false,
  typeMsg: "",
  nameErr: false,
  nameMsg: "",
  descriptionErr: false,
  descriptionMsg: "",
  documentErr: false,
  documentMsg: "",
  scopeOfWorkErr: false,
  scopeOfWorkMsg: "",
  customerNameErr: false,
  customerNameMsg: "",
  customerEmailErr: false,
  customerEmailMsg: "",

  clientTypeErr: false,
  clientTypeMsg: "",
  scopeErr: false,
  scopeMsg: "",
  unitTypeErr: false,
  unitTypeMsg: "",
  locationErr: false,
  locationMsg: "",
  unitErr: false,
  unitMsg: "",
  unitSizeErr: false,
  unitSizeMsg: "",
};

const MAX_DESCRIPTION_LENGTH = 1000;

const StyledButton = styled(Button)(({ theme }) => ({
  background: "#4D6BF6",
  color: "#ffffff",
  boxShadow: "none",
  border: "1px solid #4D6BF6",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "500",
  borderRadius: "10px",
  padding: ".5vh 1vw",
  letterSpacing: "0",
}));

const GreyStyledButton = styled(Button)(({ theme }) => ({
  background: "#fff",
  color: "#0D0D0E",
  boxShadow: "none",
  border: "1px solid #D7D8D9",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: "500",
  borderRadius: "10px",
  padding: ".5vh 1vw",
  letterSpacing: "0",
}));

const Summary = () => {
  const { id } = useParams;
  console.log("id", id);
  const navigate = useNavigate();
  const md = useMediaQuery(theme.breakpoints.down("md"));
  const { proposalDetails, setProposalDetails } = useProposalContext();
  const [expertiseList, setExpertiesList] = useState([]);
  const [errObj, setErrObj] = useState(errorObj);
  const [details, setDetails] = useState({
    project_type: "",
    name: "",
    description: "",
    scope_of_work: "",
    username: "",
    customer_email: "",
    project_image: [],
    location: "",
    client_type: "",
    unit_type: "",
    scope: "",
    unit: "",
    unit_size: "",
  });
  const [isDisabled, setIsDisabled] = useState(false);
  const [uploadLoader, setUploadLoader] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [loader, setLoader] = useState(false);
  const [projectImage, setProjectImage] = useState(null);
  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const clientTypeList = ["Airbnb", "Homeowner", "Commercial", "Retail"];
  const scopeList = ["Design", "Renovation", "Fitout / Furnishing"];
  const unitTypeList = [
    "Apartment",
    "Villa",
    "Retail",
    "Commercial",
    "Landscape",
    "Kitchen",
  ];

  useEffect(() => {
    if (proposalDetails && proposalDetails?.proposal_id) {
      getDetailsApiCall(proposalDetails?.proposal_id);
    } else {
      setIsDisabled(false);
    }
  }, [proposalDetails?.proposal_id]);

  useEffect(() => {
    getprojectList();
  }, []);

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_DESCRIPTION_LENGTH) {
      setDetails((prev) => ({
        ...prev,
        description: value,
      }));
      setErrObj({
        ...errObj,
        descriptionErr: false,
        descriptionMsg: "",
      });
    }
  };

  const handleScopeOfWorkChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_DESCRIPTION_LENGTH) {
      setDetails((prev) => ({
        ...prev,
        scope_of_work: value,
      }));
      setErrObj({
        ...errObj,
        scopeOfWorkErr: false,
        scopeOfWorkMsg: "",
      });
    }
  };

  const handleClose = () => {
    navigate("/proposals");
  };

  async function saveDetailsApiCall(data) {
    setButtonLoader(true);
    try {
      const proposalIdParam = proposalDetails?.proposal_id
        ? `?proposal_id=${proposalDetails.proposal_id}`
        : "";

      const response = await getAPIProgressData(
        `${Setting.endpoints.createDraftProposal}${proposalIdParam}`,
        "POST",
        data,
        true
      );

      if (response?.success) {
        toast.success("Details Added Successfully");
        setProposalDetails((prevDetails) => ({
          ...prevDetails,
          ...response.data,
        }));

        navigate(
          `/new-create-proposal/${response?.data?.proposal_id}/milestone`
        );
      } else {
        toast.error(response?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error(error.toString());
    } finally {
      setButtonLoader(false);
    }
  }

  async function editDetailsApiCall(data) {
    setButtonLoader(true);
    try {
      const response = await getApiData(
        `api/admin/update-project-details`,
        "PATCH",
        data,
        {
          "Content-Type": "application/json",
          Accept: "application/json",
        }
      );

      if (response?.success) {
        toast.success("Details Updated Successfully");
        navigate(
          `/new-create-proposal/${proposalDetails?.proposal_id}/milestone`
        );
      } else {
        toast.error(response?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error(error.toString());
    } finally {
      setButtonLoader(false);
    }
  }

  async function getDetailsApiCall(id) {
    setLoader(true);
    try {
      const response = await getApiData(
        `${Setting.endpoints.getProject}?proposal_id=${id}`,
        "GET",
        {}
      );
      if (
        response.success &&
        isArray(response?.data) &&
        !isEmpty(response?.data)
      ) {
        const data = response?.data[0];

        setDetails({
          project_type: data?.project_type,
          name: data?.name,
          description: data?.description,
          username: data?.username,
          customer_email: data?.customer_email,
          scope_of_work: data?.scope_of_work,
          project_image:
            isArray(data?.project_image) && !isEmpty(data?.project_image)
              ? data?.project_image
              : [],
          location: data?.location || "",
          client_type: data?.client_type || "",
          scope: data?.scope || "",
          unit_type: data?.unit_type || "",
          unit: data?.unit || "",
          unit_size: data?.unit_size || "",
        });
        setProposalDetails(data);
        setIsDisabled(true);
      }
      setLoader(false);
    } catch (error) {
      console.log("error:===>>>", error);
      setLoader(false);
    }
  }

  async function getprojectList() {
    setLoader(true);
    try {
      const response = await getApiData(
        `${Setting.endpoints.projectlist}`,
        "GET",
        {}
      );
      if (response.success) {
        setLoader(false);
        if (isArray(response?.data) && !isEmpty(response?.data)) {
          setExpertiesList(response?.data);
        } else {
          setExpertiesList([]);
        }
      }
    } catch (error) {
      setLoader(false);
      toast.error(error);
    }
  }

  function handleEdit() {
    const newErrObj = { ...errObj };
    let valid = true;

    const validations = [
      {
        field: details.client_type,
        errorKey: "clientTypeErr",
        msgKey: "clientTypeMsg",
        message: "Please select client type",
      },
      {
        field: details.location,
        errorKey: "locationErr",
        msgKey: "locationMsg",
        message: "Please select location",
      },
      {
        field: details.scope,
        errorKey: "scopeErr",
        msgKey: "scopeMsg",
        message: "Please select scope",
      },
      {
        field: details.unit_type,
        errorKey: "unitTypeErr",
        msgKey: "unitTypeMsg",
        message: "Please select unit type",
      },
      {
        field: details.unit,
        errorKey: "unitErr",
        msgKey: "unitMsg",
        message: "Please enter unit",
      },
      {
        field: details.unit_size,
        errorKey: "unitSizeErr",
        msgKey: "unitSizeMsg",
        message: "Please enter unit size",
      },
    ];

    validations.forEach(({ field, errorKey, msgKey, message }) => {
      if (!field) {
        valid = false;
        newErrObj[errorKey] = true;
        newErrObj[msgKey] = message;
      } else {
        newErrObj[errorKey] = false;
        newErrObj[msgKey] = "";
      }
    });

    setErrObj(newErrObj);

    if (!valid) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (proposalDetails?.proposal_id) {
      const editPayload = {
        project_id: proposalDetails?.id,
        data: {
          client_type: details.client_type,
          location: details.location,
          scope: details.scope,
          unit_type: details.unit_type,
          unit: details.unit,
          unit_size: details.unit_size,
        },
      };

      console.log("hit edit", editPayload);
      editDetailsApiCall(editPayload);
    }
  }

  function handleSave() {
    const newErrObj = { ...errObj };
    let valid = true;

    const validations = [
      {
        field: details.project_type,
        errorKey: "typeErr",
        msgKey: "typeMsg",
        message: "Please select project type",
      },
      {
        field: details.name?.trim(),
        errorKey: "nameErr",
        msgKey: "nameMsg",
        message: "Please enter project name",
      },
      {
        field: details.description?.trim(),
        errorKey: "descriptionErr",
        msgKey: "descriptionMsg",
        message: "Please enter project description",
      },
      {
        field: details.scope_of_work?.trim(),
        errorKey: "scopeOfWorkErr",
        msgKey: "scopeOfWorkMsg",
        message: "Please enter scope of work",
      },
      {
        field: details.username?.trim(),
        errorKey: "customerNameErr",
        msgKey: "customerNameMsg",
        message: "Please enter customer name",
      },
      {
        field:
          details.customer_email?.trim() &&
          emailRegex.test(details.customer_email),
        errorKey: "customerEmailErr",
        msgKey: "customerEmailMsg",
        message: "Please provide a valid customer email",
      },
      {
        field: details.client_type,
        errorKey: "clientTypeErr",
        msgKey: "clientTypeMsg",
        message: "Please select client type",
      },
      {
        field: details.location,
        errorKey: "locationErr",
        msgKey: "locationMsg",
        message: "Please select location",
      },
      {
        field: details.scope,
        errorKey: "scopeErr",
        msgKey: "scopeMsg",
        message: "Please select scope",
      },
      {
        field: details.unit_type,
        errorKey: "unitTypeErr",
        msgKey: "unitTypeMsg",
        message: "Please select unit type",
      },
      {
        field: details.unit,
        errorKey: "unitErr",
        msgKey: "unitMsg",
        message: "Please enter unit",
      },
      {
        field: details.unit_size,
        errorKey: "unitSizeErr",
        msgKey: "unitSizeMsg",
        message: "Please enter unit size",
      },
    ];

    validations.forEach(({ field, errorKey, msgKey, message }) => {
      if (!field) {
        valid = false;
        newErrObj[errorKey] = true;
        newErrObj[msgKey] = message;
      } else {
        newErrObj[errorKey] = false;
        newErrObj[msgKey] = "";
      }
    });
    setErrObj(newErrObj);
    if (!valid) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      const { project_image, ...restDetails } = details;
      const body = { ...restDetails };

      body.project_image = projectImage;
      console.log("hit create");
      saveDetailsApiCall(body);
      return;
    }
  }

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) {
      toast.error("No file selected");
      return;
    }

    const formData = new FormData();
    formData.append("file_1", file);
    formData.append("type", "project");
    formData.append("project_id", proposalDetails?.id);

    setUploadLoader(true);

    try {
      const response = await postFormData(
        Setting.endpoints.projectFiles,
        formData
      );
      if (response.success) {
        await getDetailsApiCall(proposalDetails?.proposal_id);
        toast.success("File uploaded successfully");
        setUploadLoader(false);
        setErrObj({
          ...errObj,
          documentErr: false,
          documentMsg: "",
        });
      } else {
        toast.error("File upload failed");
        setUploadLoader(false);
      }
    } catch (error) {
      toast.error("An error occurred during file upload");
      setUploadLoader(false);
    }
  }

  const handleDeleteImage = async () => {
    setUploadLoader(true);
    try {
      const response = await getApiData(
        `api/project/delete-files/${details?.project_image?.[0]?.id}`,
        "POST",
        {},
        { "Content-Type": "application/json" },
        false
      );
      if (response.success) {
        await getDetailsApiCall(proposalDetails?.proposal_id);
        toast.success(response?.message);
        setUploadLoader("");
      }
    } catch (error) {
      toast.error(error.toString());
      setUploadLoader("");
    }
  };

  function displayImagesView() {
    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          borderRadius: 6,
          marginBottom: 10,
          overflow: "hidden",
        }}
      >
        <img
          style={{
            width: "100%",
            height: 250,
            borderRadius: 6,
            objectFit: "cover",
          }}
          src={details?.project_image?.[0]?.image}
          alt="Primary Image"
        />
        <HighlightOffOutlined
          style={{
            position: "absolute",
            top: 5,
            right: 5,
            cursor: "pointer",
            fontSize: 28,
            color: "red",
            zIndex: 10,
          }}
          onClick={(e) => {
            handleDeleteImage();
          }}
        />
      </div>
    );
  }

  return (
    <>
      {loader ? (
        <center>
          <CircularProgress style={{ color: "#274BF1" }} size={26} />
        </center>
      ) : (
        <Stack
          overflow="auto"
          gap="8px"
          sx={{ overflowX: "hidden !important" }}
        >
          {proposalDetails?.proposal_id ? (
            <Grid
              item
              xs={12}
              style={{
                paddingTop: 10,
              }}
            >
              {uploadLoader ? (
                <Grid
                  item
                  container
                  justifyContent={"center"}
                  alignItems={"center"}
                  sx={12}
                  minHeight={220}
                >
                  <CircularProgress style={{ color: "#274BF1" }} size={26} />
                </Grid>
              ) : (
                <>
                  <InputLabel
                    shrink
                    error={errObj.documentErr}
                    style={{ fontSize: "1rem", color: "#202939" }}
                  >
                    Project Image
                  </InputLabel>
                  {details?.project_image?.length > 0 ? (
                    <Grid
                      item
                      style={{
                        width: "100%",
                      }}
                    >
                      {console.log("d", details?.project_image)}
                      {displayImagesView()}
                    </Grid>
                  ) : (
                    <Grid
                      item
                      container
                      xs={12}
                      style={{
                        position: "relative",
                        marginBottom: "5px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "100%",
                          height: 170,
                          border: "1px dashed #9CA3AF",
                          borderRadius: 4,
                          gap: 1,
                        }}
                      >
                        <div style={{ width: "24px", height: "24px" }}>
                          <img src={Images.upload_icon} alt="upload-icon"></img>
                        </div>
                        <InputLabel>
                          <b>
                            <span
                              style={{
                                cursor: "pointer",
                                color: "#2563EB",
                              }}
                            >
                              Click to upload Images
                            </span>{" "}
                            or drag and drop{" "}
                          </b>
                        </InputLabel>
                        <InputLabel style={{ fontSize: 12, color: "#6B7280" }}>
                          {"PNG, JPG, (max size 1200*800)"}
                        </InputLabel>
                      </div>
                      <input
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          opacity: 0,
                          width: "100%",
                          cursor: "pointer",
                        }}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e)}
                      />
                      <FormHelperText
                        error={errObj.documentErr}
                        style={{ fontFamily: "Inter Tight" }}
                      >
                        {errObj.documentMsg}
                      </FormHelperText>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          ) : (
            <ImageUploader
              projectImage={projectImage}
              setProjectImage={setProjectImage}
              errObj={errObj}
            />
          )}

          <Stack gap="8px">
            <Grid container flex columnGap={1} wrap={md ? "wrap" : "nowrap"}>
              <Grid item xs={6}>
                <CAutocomplete
                  label="Project Type"
                  placeholder="Select project type"
                  value={expertiseList.find(
                    (option) => option?.project_name === details?.project_type
                  )}
                  onChange={(e, newValue) => {
                    setDetails((prev) => ({
                      ...prev,
                      project_type: newValue?.project_name,
                      exp_id: newValue?.id,
                    }));
                    setErrObj({
                      ...errObj,
                      typeErr: false,
                      typeMsg: "",
                    });
                  }}
                  options={expertiseList}
                  getOptionLabel={(option) => option?.project_name}
                  error={errObj.typeErr}
                  helpertext={errObj.typeMsg}
                  disabled={proposalDetails?.proposal_id}
                />
              </Grid>
              <Grid item xs={6}>
                <CInput
                  label="Project Name"
                  placeholder="Enter project name"
                  value={details?.name}
                  onChange={(e) => {
                    setDetails((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }));
                    setErrObj({
                      ...errObj,
                      nameErr: false,
                      nameMsg: "",
                    });
                  }}
                  inputProps={{ maxLength: 50 }}
                  error={errObj.nameErr}
                  helpertext={errObj.nameMsg}
                  disabled={proposalDetails?.proposal_id}
                />
              </Grid>
            </Grid>
          </Stack>

          <Stack gap="8px" style={{ paddingTop: 10 }}>
            <CustomerSelection
              details={details}
              setDetails={setDetails}
              errObj={errObj}
              setErrObj={setErrObj}
              isDisabled={isDisabled}
              md={md}
            />
          </Stack>

          <Stack gap="8px">
            <Grid container flex columnGap={1} wrap={md ? "wrap" : "nowrap"}>
              <Grid item xs={6}>
                <SearchLocation
                  details={details}
                  setDetails={setDetails}
                  errObj={errObj}
                  setErrObj={setErrObj}
                  isDisabled={isDisabled}
                  md={md}
                />
              </Grid>
              <Grid item xs={6}>
                <CAutocomplete
                  label="Client Type"
                  placeholder="Select client type"
                  value={clientTypeList.find(
                    (option) => option === details?.client_type
                  )}
                  onChange={(e, newValue) => {
                    setDetails((prev) => ({
                      ...prev,
                      client_type: newValue,
                    }));
                    setErrObj({
                      ...errObj,
                      clientTypeErr: false,
                      clientTypeMsg: "",
                    });
                  }}
                  options={clientTypeList}
                  getOptionLabel={(option) => option}
                  error={errObj.clientTypeErr}
                  helpertext={errObj.clientTypeMsg}
                />
              </Grid>
            </Grid>
          </Stack>

          <Stack gap="8px">
            <Grid container flex columnGap={1} wrap={md ? "wrap" : "nowrap"}>
              <Grid item xs={6}>
                <CAutocomplete
                  label="Scope"
                  placeholder="Select scope"
                  value={scopeList.find((option) => option === details?.scope)}
                  onChange={(e, newValue) => {
                    setDetails((prev) => ({
                      ...prev,
                      scope: newValue,
                    }));
                    setErrObj({
                      ...errObj,
                      scopeErr: false,
                      scopeMsg: "",
                    });
                  }}
                  options={scopeList}
                  getOptionLabel={(option) => option}
                  error={errObj.scopeErr}
                  helpertext={errObj.scopeMsg}
                />
              </Grid>
              <Grid item xs={6}>
                <CAutocomplete
                  label="Unit Type"
                  placeholder="Select unit type"
                  value={unitTypeList.find(
                    (option) => option === details?.unit_type
                  )}
                  onChange={(e, newValue) => {
                    setDetails((prev) => ({
                      ...prev,
                      unit_type: newValue,
                    }));
                    setErrObj({
                      ...errObj,
                      unitTypeErr: false,
                      unitTypeMsg: "",
                    });
                  }}
                  options={unitTypeList}
                  getOptionLabel={(option) => option}
                  error={errObj.unitTypeErr}
                  helpertext={errObj.unitTypeMsg}
                />
              </Grid>
            </Grid>
          </Stack>

          <Stack gap="8px">
            <Grid container flex columnGap={1} wrap={md ? "wrap" : "nowrap"}>
              <Grid item xs={6}>
                <CInput
                  label="Unit"
                  placeholder="Enter unit"
                  value={details?.unit}
                  onChange={(e) => {
                    setDetails((prev) => ({
                      ...prev,
                      unit: e.target.value,
                    }));
                    setErrObj({
                      ...errObj,
                      unitErr: false,
                      unitMsg: "",
                    });
                  }}
                  inputProps={{ maxLength: 50 }}
                  error={errObj.unitErr}
                  helpertext={errObj.unitMsg}
                />
              </Grid>
              <Grid item xs={6}>
                <CInput
                  label="Unit Size"
                  placeholder="Enter unit size"
                  value={details?.unit_size}
                  onChange={(e) => {
                    setDetails((prev) => ({
                      ...prev,
                      unit_size: e.target.value,
                    }));
                    setErrObj({
                      ...errObj,
                      unitSizeErr: false,
                      unitSizeMsg: "",
                    });
                  }}
                  inputProps={{ maxLength: 10 }}
                  error={errObj.unitSizeErr}
                  helpertext={errObj.unitSizeMsg}
                />
              </Grid>
            </Grid>
          </Stack>

          <Grid item xs={12} style={{ paddingTop: 10 }}>
            <CInput
              multiline
              rows={4}
              label="Description"
              placeholder="Provide project description (Max upto 1000 characters allowed)"
              value={details?.description}
              onChange={handleDescriptionChange}
              error={errObj.descriptionErr}
              helpertext={
                errObj.descriptionErr
                  ? errObj.descriptionMsg
                  : `${
                      MAX_DESCRIPTION_LENGTH - details?.description?.length
                    } characters left`
              }
              disabled={proposalDetails?.proposal_id}
            />
          </Grid>

          <Grid item xs={12} style={{ paddingTop: 10 }}>
            <CInput
              multiline
              rows={4}
              label="Scope of work"
              placeholder="Write here (Max upto 1000 characters allowed)"
              value={details?.scope_of_work}
              onChange={handleScopeOfWorkChange}
              error={errObj.scopeOfWorkErr}
              helpertext={
                errObj.scopeOfWorkErr
                  ? errObj.scopeOfWorkMsg
                  : `${
                      MAX_DESCRIPTION_LENGTH - details?.scope_of_work?.length
                    } characters left`
              }
              disabled={proposalDetails?.proposal_id}
            />
          </Grid>

          <Stack
            padding="10px"
            alignItems="end"
            flexDirection="row"
            justifyContent="flex-end"
            gap="10px"
          >
            <GreyStyledButton onClick={handleClose}>Cancel</GreyStyledButton>
            <StyledButton
              variant="contained"
              onClick={proposalDetails?.id ? handleEdit : handleSave}
            >
              {buttonLoader ? (
                <CircularProgress size={26} style={{ color: "#fff" }} />
              ) : (
                "Save & Continue"
              )}
            </StyledButton>
          </Stack>
        </Stack>
      )}
    </>
  );
};

export default Summary;
